// Layer 3 (Transformation) for the Equipment Health tab's Condition detail
// panel (a20-maintenance S6b). Pure: no React, no engine mutation, no side
// effect — every field is a read of the condition-monitoring inventory, the
// historian series behind the selected point, that series' own Reading, or the
// item seed the point joins to.
//
// The panel is a *read* of one monitoring point. Nothing here writes: a
// reading is the historian's observation, the references are the Reading's own
// `band.hi` and `spec.hi`, and the days figure is the window's own slope
// projected to that limit. There is no typed threshold, no caption sentence
// and no reading-entry field — OptiFlow reads the historian, it does not write
// to it (ADR-0002).
//
// Absence is real absence, never a placeholder: a flat or falling window has no
// days figure (the panel renders nothing where the number would sit), a point
// with fewer than two observations has no chart, and a point outside the join
// table has no item line.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesCadence, SeriesTime } from '@/versions/v1/schema/reading-series';
import type { TrendGap, TrendReference, TrendSeries, VizTone } from '@/versions/v1/modules/_viz';
import { addMinutes, diffMinutes, formatClockShort, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { sampleGaps } from '@/versions/v1/shared/series-gaps.derive';
import { SERIES_BY_ID, seriesAt } from '@/versions/v1/shared/series.derive';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { BM01_BEARING_STOP_HOURS } from '@/versions/v1/shared/bunker-hill-bearing-stop';
import { bm01BearingTrend } from '@/versions/v1/modules/downtime/ValueLost.transform';
import { caseAffectsRead } from '@/versions/v1/modules/overview/CaseAffects.transform';
import { RISK_ENTRIES } from '@/versions/v1/modules/risk/seed';
import { DOWNTIME_STATES } from './downtime.seed';
import {
  CM_POINT_ITEM,
  CONDITION_LABEL,
  MAINTAINABLE_ITEMS,
  type CriticalityTier,
  type FmeaTaskType,
} from './strategy.seed';
import { conditionTone, itemSubprocessId } from './Strategy.transform';
import {
  CM_SERIES_BY_POINT,
  plantConstraint,
  type ConditionMonitoringPoint,
} from './Scene.transform';

/** The rolling window an hourly series is read over — the same seven days the
 *  points table's own `cm-2` trend readout uses. Slower cadences read every
 *  visible observation: a six-week daily drift and an eight-day daily sample
 *  are already short enough to draw whole. */
const HOURLY_WINDOW_DAYS = 7;

export type ConditionDetailRead = {
  pointId: string;
  title: string;
  item: { id: string; label: string; criticality: CriticalityTier; taskType: FmeaTaskType } | null;
  chip: string | null;
  chipLabel: string | null;
  chipTone: 'danger' | 'warning' | 'default';
  lineTone: VizTone;
  monitoringType: string;
  frequency: string;
  seriesId: string;
  reading: { id: string; tag: string; label: string; unit: string; decimals: number } | null;
  chart: {
    categories: string[];
    series: TrendSeries[];
    references: TrendReference[];
    /** The window's missing-sample runs (W3e, #650), located in the chart's own
     *  category indices so the drawn line breaks across the WO-441 trip. */
    gaps: TrendGap[];
  } | null;
  latest: { value: number; valueLabel: string; at: SeriesTime; atLabel: string } | null;
  daysToLimit: { limit: number; limitLabel: string; days: number; daysLabel: string } | null;
  caseId?: string;
  caseTitle: string | null;
  provenance: string;
  /** polish.P21 — the panel-row read (comp A): the heading, the time-axis plot
   *  with its forward half, and the four figures under it. */
  panel: ConditionPanelRead;
};

/** One figure under the trend (comp `.figs`): a caps label, a value, and the
 *  comparison that says what the value is measured against. */
export type ConditionFigure = {
  id: 'latest' | 'rate' | 'limit' | 'affects';
  label: string;
  value: string;
  sub: string;
  tone?: 'bad';
};

export type ConditionPlot = {
  /** The drawn time axis: the read window, stretched past the cursor to the
   *  projected limit when the point has one. */
  window: { startAt: SeriesTime; endAt: SeriesTime };
  /** The visible samples, nulls kept so the line breaks across a gap. */
  points: readonly { at: SeriesTime; value: number | null }[];
  yDomain: { min: number; max: number };
  yTicks: readonly number[];
  dayTicks: readonly { at: SeriesTime; label: string }[];
  /** The missing-sample runs, each labelled by the stoppage the downtime log
   *  holds across it (`Trip · WO-441`), or unlabelled when the log holds none. */
  gaps: readonly { startAt: SeriesTime; endAt: SeriesTime; label: string | null }[];
  bandHigh: number | null;
  /** The current run above the band high, tinted on the chart. */
  aboveBand: readonly { at: SeriesTime; value: number }[];
  latest: { at: SeriesTime; value: number } | null;
  now: { at: SeriesTime; label: string } | null;
  projection: { fromAt: SeriesTime; fromValue: number; toAt: SeriesTime; toValue: number; label: string } | null;
  /** The case's proposed stop, drawn as a draft block on the forward half. */
  stop: { startAt: SeriesTime; endAt: SeriesTime; label: string } | null;
};

export type ConditionPanelRead = {
  /** `BM01 trunnion bearing · TE-622-625` — the point and its instrument tag. */
  heading: string;
  /** `Grinding · constraint` when the point's item sits on the plant's constraint. */
  constraintChip: string | null;
  /** `hourly to 08 Jun 11:00 · projected at the post-restart rate`. */
  window: string;
  plot: ConditionPlot | null;
  figures: readonly ConditionFigure[];
};

/** The condition pill's tone — S6a's one decode, not a second one. */
export function seriesTone(chip: string | null): VizTone {
  if (chip === 'warn') return 'bad';
  if (chip === 'rising') return 'warn';
  if (chip === 'stable') return 'ok';
  return 'neutral';
}

/** Days from `last` to `limit` at the window's own first-to-last rate.
 *  A flat or falling window never reaches the limit: return `null`, and the
 *  caller draws nothing — absence is not an `n/a` figure. A window already at
 *  or past the limit is `0`, which is a figure. */
export function daysToLimit(
  first: { at: SeriesTime; value: number },
  last: { at: SeriesTime; value: number },
  limit: number,
): number | null {
  const minutes = diffMinutes(last.at, first.at);
  if (minutes <= 0) return null;
  const slopePerDay = (last.value - first.value) / (minutes / (24 * 60));
  if (slopePerDay <= 0) return null;
  return Math.max(0, (limit - last.value) / slopePerDay);
}

/** One monitoring point, read against its own series. `null` when the id is
 *  not a point the tab carries, or when the point names a series the seed does
 *  not hold — never a half-built read with an empty chart. */
export function conditionDetail(
  pointId: string,
  points: readonly ConditionMonitoringPoint[],
  state: SessionState,
): ConditionDetailRead | null {
  const point = points.find((row) => row.id === pointId);
  if (!point) return null;
  const seriesId = CM_SERIES_BY_POINT[pointId];
  if (!seriesId) return null;
  const series = SERIES_BY_ID[seriesId];
  if (!series) return null;

  const view = seriesAt(seriesId, state);
  const windowFrom = addMinutes(state.cursor, -HOURLY_WINDOW_DAYS * 24 * 60);
  // The visible window with its null samples kept: the chart draws only the
  // observed points, but the gap derivation must see the run the historian
  // missed (W3e).
  const visible = view.points.filter(
    (p) =>
      diffMinutes(p.at, state.cursor) <= 0 &&
      (view.cadence !== 'hourly' || diffMinutes(p.at, windowFrom) >= 0),
  );
  const window = visible.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );

  const readingRow = series.readingIds[0] ? readingByIdLookup(series.readingIds[0]) : undefined;
  // The Reading's own precision; one decimal is the kit's fallback for a
  // reading that declares none (the series still carries raw values).
  const decimals = readingRow?.decimals ?? 1;
  const reading = readingRow
    ? {
        id: readingRow.id,
        tag: readingRow.tag,
        label: readingRow.label,
        unit: readingRow.unit,
        decimals,
      }
    : null;
  const unit = readingRow?.unit ?? '';

  const references: TrendReference[] = [];
  const warnHi = readingRow?.band?.hi;
  const limitHi = readingRow?.spec?.hi;
  if (warnHi !== undefined) {
    references.push({ value: warnHi, label: `Warn ${warnHi} ${unit}`.trim(), tone: 'warn' });
  }
  if (limitHi !== undefined) {
    references.push({ value: limitHi, label: `Limit ${limitHi} ${unit}`.trim(), tone: 'bad' });
  }

  const first = window[0];
  const last = window[window.length - 1];
  // Missing-sample runs inside the window, located in the drawn series' own
  // indices: `startAt`/`endAt` bound the run (the samples either side) and the
  // indices are those two samples' positions in the chart's categories, so the
  // line breaks across exactly the trip the historian did not record.
  const gaps: TrendGap[] = sampleGaps(visible).flatMap((gap) => {
    const startIndex = window.findIndex((p) => p.at === gap.startAt);
    const endIndex = window.findIndex((p) => p.at === gap.endAt);
    return startIndex >= 0 && endIndex > startIndex ? [{ ...gap, startIndex, endIndex }] : [];
  });
  const chart =
    window.length >= 2 && first !== undefined && last !== undefined
      ? {
          // One label per point: the chart thins the axis itself at its own
          // stride, so a `''` gap would land a blank label on a stride index.
          categories: window.map((p) => formatDayMonth(p.at)),
          series: [
            {
              name: readingRow?.label ?? point.item,
              points: window.map((p) => p.value),
              tone: seriesTone(point.statusChip ?? null),
            },
          ],
          references,
          gaps,
        }
      : null;

  const latest =
    view.latest === undefined
      ? null
      : {
          value: view.latest.value,
          valueLabel: readingRow
            ? `${view.latest.value.toFixed(decimals)} ${readingRow.unit}`
            : view.latest.value.toFixed(1),
          at: view.latest.at,
          atLabel: formatClockShort(view.latest.at),
        };

  let days: ConditionDetailRead['daysToLimit'] = null;
  if (limitHi !== undefined && first !== undefined && last !== undefined) {
    const bearingTrend = pointId === 'cm-2' ? bm01BearingTrend(state) : null;
    // BM01's horizon is the shared post-restart trend; before it can be fitted
    // the horizon is unmeasured, never the window's own rate across the trip
    // (#782). Every other point keeps the window rate.
    const bearingFit = bearingTrend?.state === 'fitted' ? bearingTrend : null;
    const value = bearingTrend
      ? bearingFit?.daysToLimit ?? null
      : daysToLimit(first, last, limitHi);
    if (value !== null) {
      days = {
        limit: bearingFit?.limitC ?? limitHi,
        limitLabel: `Limit ${limitHi} ${unit}`.trim(),
        days: value,
        daysLabel: bearingFit
          ? `≈ ${value.toFixed(1)} d · ${bearingFit.limitDate}`
          : `≈ ${Math.round(value)} d`,
      };
    }
  }

  const itemId = CM_POINT_ITEM[pointId];
  const itemRow = itemId ? MAINTAINABLE_ITEMS.find((row) => row.id === itemId) : undefined;
  const item = itemRow
    ? {
        id: itemRow.id,
        label: itemRow.label,
        criticality: itemRow.criticality,
        taskType: itemRow.taskType,
      }
    : null;

  const chip = point.statusChip ?? null;

  const panel = conditionPanel({
    point,
    state,
    visible,
    window,
    reading,
    warnHi,
    limitHi,
    latest,
    days,
    itemId,
  });

  return {
    pointId,
    title: point.item,
    item,
    chip,
    chipLabel: chip ? (CONDITION_LABEL[chip] ?? chip) : null,
    chipTone: conditionTone(chip),
    lineTone: seriesTone(chip),
    monitoringType: point.monitoringType,
    frequency: point.frequency,
    seriesId,
    reading,
    chart,
    latest,
    daysToLimit: days,
    caseId: point.caseId,
    caseTitle: CASES.find((row) => row.id === point.caseId)?.title ?? null,
    provenance: series.provenance,
    panel,
  };
}

// ---------------------------------------------------------------------------
// The panel-row read (polish.P21, comp A: the locked equipment-beat Condition A
// chart). Every mark is a read of the same window the table and the case page
// read: the samples and their gaps, the Reading's band and spec, the shared
// BM01 post-settle trend (rate, horizon, limit instant), the downtime log's
// stoppage across the gap, the case's proposed stop and the item's own join to
// the plant constraint. Nothing is typed here.
// ---------------------------------------------------------------------------

function dayClock(at: SeriesTime): string {
  return `${formatDayMonth(at)} ${at.slice(11, 16)}`;
}

function signed(value: number, decimals: number): string {
  return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(decimals)}`;
}

function nextMidnight(at: SeriesTime): SeriesTime {
  const floor = `${at.slice(0, 10)}T00:00` as SeriesTime;
  return floor === at ? at : addMinutes(floor, 24 * 60);
}

/** The smallest of 1, 2, 2.5 or 5 × 10ⁿ that splits the span into at most
 *  five steps — the gridline spacing the chart ticks at. */
function niceStep(span: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(span / 5));
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * magnitude);
  return candidates.find((step) => span / step <= 5) ?? 10 * magnitude;
}

/** Midnight ticks across the window, each day labelled. */
function midnightTicks(startAt: SeriesTime, endAt: SeriesTime): { at: SeriesTime; label: string }[] {
  const ticks: { at: SeriesTime; label: string }[] = [];
  for (let at = nextMidnight(startAt); at <= endAt; at = addMinutes(at, 24 * 60)) {
    ticks.push({ at, label: formatDayMonth(at) });
  }
  return ticks;
}

/** The start of the run of samples above `bandHigh` that is still running at
 *  the window's last sample — `above 80 °C since 07 Jun 03:00`. A gap ends a
 *  run, so a trip never joins two runs into one. */
export function currentRunAbove(
  points: readonly { at: SeriesTime; value: number | null }[],
  bandHigh: number,
): { at: SeriesTime; value: number }[] {
  const run: { at: SeriesTime; value: number }[] = [];
  for (const point of points) {
    if (point.value === null || point.value <= bandHigh) {
      run.length = 0;
      continue;
    }
    run.push({ at: point.at, value: point.value });
  }
  return run;
}

function conditionPanel(input: {
  point: ConditionMonitoringPoint;
  state: SessionState;
  visible: readonly { at: SeriesTime; value: number | null }[];
  window: readonly { at: SeriesTime; value: number }[];
  reading: ConditionDetailRead['reading'];
  warnHi: number | undefined;
  limitHi: number | undefined;
  latest: ConditionDetailRead['latest'];
  days: ConditionDetailRead['daysToLimit'];
  itemId: string | undefined;
}): ConditionPanelRead {
  const { point, state, visible, window, reading, warnHi, limitHi, latest, days, itemId } = input;
  const unit = reading?.unit ?? '';
  const decimals = reading?.decimals ?? 1;
  const first = window[0];
  const last = window[window.length - 1];
  const bearing = point.id === 'cm-2' ? bm01BearingTrend(state) : null;
  const bearingFit = bearing?.state === 'fitted' ? bearing : null;
  const caseItem = point.caseId ? CASES.find((row) => row.id === point.caseId) : undefined;
  const item = itemId ? MAINTAINABLE_ITEMS.find((row) => row.id === itemId) : undefined;

  // The constraint chip: the item's subprocess is the capacity engine's
  // bottleneck circuit (reconciliation ADOPT #6).
  const constraint = plantConstraint(state);
  const constraintChip = item && constraint.subprocessIds.includes(itemSubprocessId(item) ?? '')
    ? `${constraint.circuit} · constraint`
    : null;

  // The forward half: the limit instant (the shared bearing trend for BM01,
  // the window's own rate otherwise) and the case's proposed stop.
  const limitAt = bearingFit?.limitAt ?? (days && latest ? addMinutes(state.cursor, days.days * 24 * 60) : null);
  const projection = limitAt && last && limitHi !== undefined
    ? {
        fromAt: last.at,
        fromValue: last.value,
        toAt: limitAt,
        toValue: limitHi,
        label: `${limitHi} ${unit} · ${dayClock(limitAt)}`.trim(),
      }
    : null;
  const stopWindow = caseItem?.stopPlan?.window;
  const stopHours = caseItem?.stopPlan ? BM01_BEARING_STOP_HOURS.plannedInspection : null;
  const stop = stopWindow && stopHours !== null && projection
    ? {
        startAt: stopWindow.at as SeriesTime,
        endAt: addMinutes(stopWindow.at as SeriesTime, stopHours * 60),
        label: `Proposed stop · ${stopWindow.label} · ${stopHours} h`,
      }
    : null;

  let plot: ConditionPlot | null = null;
  if (first && last) {
    const startAt = first.at;
    const forwardEnd = projection ? projection.toAt : null;
    const endAt = forwardEnd ? nextMidnight(addMinutes(forwardEnd, 2 * 24 * 60)) : last.at;
    const values = window.map((p) => p.value);
    const refs = [warnHi, limitHi].filter((v): v is number => v !== undefined);
    const lo = Math.min(...values, ...refs);
    const hi = Math.max(...values, ...refs);
    const span = Math.max(hi - lo, 1e-6);
    const step = niceStep(span);
    const yDomain = { min: lo - span * 0.25, max: hi + span * 0.1 };
    const yTicks: number[] = [];
    for (let tick = Math.ceil(yDomain.min / step) * step; tick <= yDomain.max; tick += step) {
      yTicks.push(Number(tick.toFixed(6)));
    }
    const gaps = sampleGaps(visible).map((gap) => {
      const stoppage = DOWNTIME_STATES.find(
        (row) =>
          itemId !== undefined &&
          row.itemId === itemId &&
          row.state === 'offline_breakdown' &&
          row.start < gap.endAt &&
          row.end > gap.startAt,
      );
      return {
        ...gap,
        label: stoppage ? `Trip${stoppage.workOrderId ? ` · ${stoppage.workOrderId}` : ''}` : null,
      };
    });
    plot = {
      window: { startAt, endAt },
      points: visible.map((p) => ({ at: p.at, value: p.value })),
      yDomain,
      yTicks,
      dayTicks: midnightTicks(startAt, endAt),
      gaps,
      bandHigh: warnHi ?? null,
      aboveBand: warnHi !== undefined ? currentRunAbove(visible, warnHi) : [],
      latest: last ? { at: last.at, value: last.value } : null,
      now: projection ? { at: state.cursor, label: `Now · ${dayClock(state.cursor)}` } : null,
      projection,
      stop,
    };
  }

  const figures: ConditionFigure[] = [];
  if (latest) {
    figures.push({ id: 'latest', label: 'Latest', value: latest.valueLabel, sub: latest.atLabel });
  }
  const runAbove = warnHi !== undefined ? currentRunAbove(visible, warnHi) : [];
  if (bearingFit) {
    figures.push({
      id: 'rate',
      label: `Since ${formatDayMonth(bearingFit.restartAt)} restart`,
      value: `${signed(bearingFit.slopeCPerDay, 1)} ${unit}/day`,
      sub: `${bearingFit.restartValue.toFixed(decimals)} → ${bearingFit.currentValue.toFixed(decimals)} ${unit}${
        runAbove[0] && warnHi !== undefined ? ` · above ${warnHi} ${unit} since ${dayClock(runAbove[0].at)}` : ''
      }`,
    });
  } else if (bearing) {
    // The cursor sits before the post-restart samples can be fitted: the trend
    // figure reads the closed honest state, and the limit reference keeps the
    // comparison (#782).
    figures.push({ id: 'rate', label: 'Bearing trend', value: 'unmeasured', sub: '' });
  } else if (first && last) {
    figures.push({
      id: 'rate',
      label: `Since ${formatDayMonth(first.at)}`,
      value: `${signed(last.value - first.value, decimals)} ${unit}`.trim(),
      sub: `${first.value.toFixed(decimals)} → ${last.value.toFixed(decimals)} ${unit}`.trim(),
    });
  }
  if (days) {
    figures.push({
      id: 'limit',
      label: days.limitLabel,
      value: days.daysLabel,
      sub: bearingFit ? 'at the post-restart rate' : 'at the window’s rate',
      tone: 'bad',
    });
  }
  const affects = caseItem ? caseAffectsRead(caseItem, state) : undefined;
  if (affects) {
    const risk = item?.riskId ? RISK_ENTRIES.find((row) => row.id === item.riskId) : undefined;
    const fmea = risk?.failureModeId ? `FMEA ${risk.failureModeId} · RPN ${risk.rpn}` : null;
    figures.push({
      id: 'affects',
      label: 'Affects',
      value: affects.kpiLabel,
      sub: [`in ${affects.lead}`, fmea].filter(Boolean).join(' · '),
    });
  }

  const cadence = seriesCadenceLabel(point.id);
  const windowLabel = [
    latest && cadence ? `${cadence} to ${dayClock(latest.at)}` : null,
    projection ? (bearingFit ? 'projected at the post-restart rate' : 'projected at the window’s rate') : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    heading: reading?.tag ? `${point.item} · ${reading.tag}` : point.item,
    constraintChip,
    window: windowLabel,
    plot,
    figures,
  };
}

const CADENCE_LABEL: Record<SeriesCadence, string> = { hourly: 'hourly', shift: 'per shift', daily: 'daily' };

/** The series' cadence as the panel names it (`hourly`, `daily`, `per shift`). */
function seriesCadenceLabel(pointId: string): string {
  const seriesId = CM_SERIES_BY_POINT[pointId];
  const cadence = seriesId ? SERIES_BY_ID[seriesId]?.cadence : undefined;
  return cadence ? CADENCE_LABEL[cadence] : '';
}
